/**
 * Firebase Push Notification Service for GuapGoals
 * Handles FCM token retrieval, permission requests, and notification management
 */
import { initializeApp, getApps } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';

// Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyAmF3a5gKYcwmVNa12kirzS4Qxhs9499BU",
  authDomain: "guapgoals-45a78.firebaseapp.com",
  projectId: "guapgoals-45a78",
  storageBucket: "guapgoals-45a78.firebasestorage.app",
  messagingSenderId: "486727181702",
  appId: "1:486727181702:web:6a284aa8b9e2132d717807",
  measurementId: "G-RBVXQD3YTH"
};

// VAPID key for web push
const VAPID_KEY = "yscgm-n3jV_qhZNKvQHI0oEm0tn41aGD4kxRNVb-o9g";

const API_URL = process.env.REACT_APP_BACKEND_URL;

let firebaseApp = null;
let messaging = null;

/**
 * Initialize Firebase app (singleton)
 */
export function initializeFirebase() {
  if (!firebaseApp) {
    firebaseApp = getApps()[0] || initializeApp(firebaseConfig);
  }
  return firebaseApp;
}

/**
 * Get Firebase Messaging instance
 */
export function getFirebaseMessaging() {
  if (!messaging) {
    const app = initializeFirebase();
    messaging = getMessaging(app);
  }
  return messaging;
}

/**
 * Check if browser supports push notifications
 */
export function isPushSupported() {
  return 'Notification' in window && 
         'serviceWorker' in navigator && 
         'PushManager' in window;
}

/**
 * Get current notification permission status
 */
export function getNotificationPermission() {
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission; // 'granted', 'denied', or 'default'
}

/**
 * Request notification permission from user
 * @returns {Promise<string>} Permission result: 'granted', 'denied', or 'default'
 */
export async function requestNotificationPermission() {
  if (!isPushSupported()) {
    console.warn('Push notifications not supported in this browser');
    return 'unsupported';
  }
  
  const permission = await Notification.requestPermission();
  console.log('Notification permission:', permission);
  return permission;
}

/**
 * Register Firebase service worker
 */
async function registerServiceWorker() {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
      console.log('FCM Service Worker registered:', registration.scope);
      return registration;
    } catch (error) {
      console.error('FCM Service Worker registration failed:', error);
      return null;
    }
  }
  return null;
}

/**
 * Get FCM token and register with backend
 * @param {string} authToken - User's authentication token
 * @returns {Promise<{success: boolean, token?: string, error?: string}>}
 */
export async function setupPushNotifications(authToken) {
  if (!isPushSupported()) {
    return { success: false, error: 'Push notifications not supported' };
  }
  
  // Check permission
  const permission = await requestNotificationPermission();
  if (permission !== 'granted') {
    return { success: false, error: `Permission ${permission}` };
  }
  
  try {
    // Register FCM service worker
    const swRegistration = await registerServiceWorker();
    
    // Initialize Firebase messaging
    const messagingInstance = getFirebaseMessaging();
    
    // Get FCM token
    const fcmToken = await getToken(messagingInstance, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: swRegistration
    });
    
    if (!fcmToken) {
      return { success: false, error: 'Failed to get FCM token' };
    }
    
    console.log('FCM Token obtained:', fcmToken.substring(0, 20) + '...');
    
    // Register token with backend
    const response = await fetch(`${API_URL}/api/push/register-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      },
      body: JSON.stringify({
        token: fcmToken,
        platform: 'web'
      })
    });
    
    if (!response.ok) {
      const error = await response.json();
      return { success: false, error: error.detail || 'Failed to register token' };
    }
    
    // Store token locally for later reference
    localStorage.setItem('fcm_token', fcmToken);
    
    return { success: true, token: fcmToken };
    
  } catch (error) {
    console.error('Push notification setup error:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Unregister FCM token (on logout)
 * @param {string} authToken - User's authentication token
 */
export async function unregisterPushNotifications(authToken) {
  const fcmToken = localStorage.getItem('fcm_token');
  if (!fcmToken) return;
  
  try {
    await fetch(`${API_URL}/api/push/unregister-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      },
      body: JSON.stringify({ token: fcmToken })
    });
    
    localStorage.removeItem('fcm_token');
    console.log('FCM token unregistered');
  } catch (error) {
    console.error('Failed to unregister FCM token:', error);
  }
}

/**
 * Setup foreground message handler
 * @param {Function} callback - Function to call when notification received in foreground
 */
export function onForegroundMessage(callback) {
  if (!isPushSupported()) return () => {};
  
  try {
    const messagingInstance = getFirebaseMessaging();
    return onMessage(messagingInstance, (payload) => {
      console.log('Foreground message received:', payload);
      
      // Show notification manually when app is in foreground
      if (Notification.permission === 'granted') {
        const notification = new Notification(
          payload.notification?.title || 'GuapGoals',
          {
            body: payload.notification?.body || 'New notification',
            icon: 'https://customer-assets.emergentagent.com/job_budget-buddy-4768/artifacts/qhory5fo_Guap.png',
            tag: payload.data?.tag || 'foreground-notification',
            data: payload.data
          }
        );
        
        notification.onclick = () => {
          window.focus();
          if (payload.data?.url) {
            window.location.href = payload.data.url;
          }
        };
      }
      
      // Call user callback
      if (callback) callback(payload);
    });
  } catch (error) {
    console.error('Error setting up foreground message handler:', error);
    return () => {};
  }
}

/**
 * Check notification status with backend
 * @param {string} authToken - User's authentication token
 * @returns {Promise<{notifications_enabled: boolean, active_devices: number}>}
 */
export async function getNotificationStatus(authToken) {
  try {
    const response = await fetch(`${API_URL}/api/push/status`, {
      headers: {
        'Authorization': `Bearer ${authToken}`
      }
    });
    
    if (response.ok) {
      return await response.json();
    }
    return { notifications_enabled: false, active_devices: 0 };
  } catch (error) {
    console.error('Failed to get notification status:', error);
    return { notifications_enabled: false, active_devices: 0 };
  }
}

export default {
  initializeFirebase,
  isPushSupported,
  getNotificationPermission,
  requestNotificationPermission,
  setupPushNotifications,
  unregisterPushNotifications,
  onForegroundMessage,
  getNotificationStatus
};
